import { faImage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import ArrowForward from '../../../assets/icons/arrow-forward.svg?react';
import type { UploadedPhoto } from '../../../utils/photoUpload';

interface ChatInputProps {
  messageText: string;
  pendingPhotos: UploadedPhoto[];
  uploadingPhoto: boolean;
  photoUploadError: string | null;
  sendingMessage: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onMessageChange: (text: string) => void;
  onSendMessage: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onPhotoButtonClick: () => void;
  onPhotoUpload: (files: FileList | null) => void;
  onRemovePhoto: (index: number) => void;
}

// ─── Styled components ────────────────────────────────────────────────────────

const MessageInputRoot = styled('div')(({ theme }) => ({
  padding: theme.baseUnit * 2,
  borderTop: `1px solid ${theme.colors.gray[200]}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.baseUnit,
}));

const InputContainer = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const InputField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.baseUnit * 3,
    fontSize: 14,
    '& fieldset': { borderColor: theme.colors.gray[300] },
    '&:hover fieldset': { borderColor: theme.colors.gray[400] },
    '&.Mui-focused fieldset': { borderColor: theme.colors.alias.primary },
  },
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.colors.alias.primary,
  color: theme.colors.alias.textContrast,
  width: 40,
  height: 40,
  '&:hover': { backgroundColor: theme.colors.alias.primary },
  '&:disabled': {
    backgroundColor: theme.colors.gray[300],
    color: theme.colors.gray[500],
  },
}));

const PhotoButton = styled(IconButton)(({ theme }) => ({
  color: theme.colors.gray[600],
  '&:hover': { backgroundColor: theme.colors.gray[100] },
}));

const HiddenFileInput = styled('input')({ display: 'none' });

const PhotoPreviewContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.baseUnit,
  padding: `0 ${theme.baseUnit * 2}px`,
  marginBottom: theme.baseUnit,
}));

const PhotoPreviewItem = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 60,
  height: 60,
  borderRadius: theme.baseUnit,
  overflow: 'visible',
  border: `1px solid ${theme.colors.gray[300]}`,
  margin: theme.baseUnit / 2,
}));

const PhotoPreviewImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  cursor: 'pointer',
  backgroundColor: theme.colors.alias.mainSurface,
}));

const PhotoRemoveButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -(theme.baseUnit / 2),
  right: -(theme.baseUnit / 2),
  width: 20,
  height: 20,
  backgroundColor: theme.colors.red[500],
  color: theme.colors.alias.textContrast,
  fontSize: 12,
  zIndex: 10,
  '&:hover': { backgroundColor: theme.colors.red[600] },
}));

// ─── PhotoPreview ─────────────────────────────────────────────────────────────

interface PhotoPreviewProps {
  photos: UploadedPhoto[];
  onRemove: (index: number) => void;
}

const PhotoPreview: React.FC<PhotoPreviewProps> = ({ photos, onRemove }) => {
  if (photos.length === 0) {
    return null;
  }

  return (
    <PhotoPreviewContainer>
      {photos.map((photo, index) => (
        <PhotoPreviewItem key={photo.localUrl}>
          <PhotoPreviewImage
            src={photo.localUrl}
            alt={`Preview ${index + 1}`}
          />
          <PhotoRemoveButton
            size="small"
            onClick={() => onRemove(index)}
            aria-label="Remove photo"
          >
            ×
          </PhotoRemoveButton>
        </PhotoPreviewItem>
      ))}
    </PhotoPreviewContainer>
  );
};

// ─── ChatInput ────────────────────────────────────────────────────────────────

const ChatInput: React.FC<ChatInputProps> = ({
  messageText,
  pendingPhotos,
  uploadingPhoto,
  photoUploadError,
  sendingMessage,
  fileInputRef,
  onMessageChange,
  onSendMessage,
  onKeyPress,
  onPhotoButtonClick,
  onPhotoUpload,
  onRemovePhoto,
}) => (
  <>
    <PhotoPreview photos={pendingPhotos} onRemove={onRemovePhoto} />

    {photoUploadError && (
      <Typography
        variant="caption"
        color="error"
        sx={{ px: 2, pb: 0.5, display: 'block' }}
      >
        {photoUploadError}
      </Typography>
    )}

    <MessageInputRoot>
      <PhotoButton onClick={onPhotoButtonClick} disabled={uploadingPhoto}>
        {uploadingPhoto ? (
          <CircularProgress size={20} />
        ) : (
          <FontAwesomeIcon icon={faImage} size="sm" />
        )}
      </PhotoButton>

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => onPhotoUpload(e.target.files)}
      />

      <InputContainer>
        <InputField
          value={messageText}
          onChange={(e) => onMessageChange(e.target.value)}
          onKeyPress={onKeyPress}
          placeholder={
            pendingPhotos.length > 0
              ? 'Remove photos to add text...'
              : 'Type a message...'
          }
          multiline
          maxRows={3}
          variant="outlined"
          size="small"
          disabled={pendingPhotos.length > 0}
        />
      </InputContainer>

      <SendButton
        onClick={onSendMessage}
        disabled={
          (!messageText.trim() && pendingPhotos.length === 0) ||
          sendingMessage ||
          uploadingPhoto
        }
        aria-label="Send"
      >
        {sendingMessage ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <ArrowForward />
        )}
      </SendButton>
    </MessageInputRoot>
  </>
);

export default ChatInput;
